import { toast } from '@heroui/react';
import { useState, useMemo } from 'react';

import type { SortingState, PaginationState } from '@hs/ui';
import { AlertDialog, Button, Chip, Dropdown, Label } from '@heroui/react';
import {
  IconPlus as Plus,
  IconRefresh as RefreshCw,
  IconDotsVertical as MoreVertical,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { ProductEditorModal } from './ProductEditorModal';
import { SkuListModal } from './SkuListModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type Product = {
  id: string;
  code?: string | null;
  name?: string | null;
  type?: string | null;
  targetScope?: string | null;
  status?: string | null;
  metadata?: Record<string, unknown> | null;
  createdAt?: string;
};

const PRODUCT_SORT_FIELDS = new Set(['code', 'name', 'type', 'targetScope', 'status']);

export function ProductListPanel() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 });
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [priceModalOpen, setPriceModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];
  const sortBy =
    sortItem && PRODUCT_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as 'code' | 'name' | 'type' | 'targetScope' | 'status')
      : undefined;

  const { data, isLoading, refetch } = trpc.bizProduct.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      ...(sortBy
        ? { sortBy, sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const) }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const products = (data?.items ?? []) as Product[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteProductMutation = trpc.bizProduct.delete.useMutation({
    onSuccess: () => {
      toast.success('产品删除成功');
      setDeletingProductId(null);
      setDeleteModalOpen(false);
      setProductToDelete(null);
      void utils.bizProduct.list.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
      setDeletingProductId(null);
    },
  });

  const handleCreate = () => {
    setEditingProduct(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingProduct(null);
    void refetch();
  };

  const handlePriceSetting = (product: Product) => {
    setSelectedProduct(product);
    setPriceModalOpen(true);
  };

  const handleDelete = (product: Product) => {
    setProductToDelete(product);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    setDeletingProductId(productToDelete.id);
    try {
      await deleteProductMutation.mutateAsync({ id: productToDelete.id });
    } catch {
      /* onError */
    }
  };

  const columnOverrides = useMemo(
    () => ({
      status: {
        cell: (row: Product) => (
          <Chip color={row.status === 'ACTIVE' ? 'success' : 'default'} size="sm" variant="soft">
            <Chip.Label>{row.status ?? '—'}</Chip.Label>
          </Chip>
        ),
      },
    }),
    []
  );

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<Product>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="product"
          data={products}
          recordCount={total}
          pageCount={pageCount}
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          columnOverrides={columnOverrides}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <div className="flex flex-wrap items-center gap-2">
              <Button onPress={handleCreate}>
                <Plus className="size-4" stroke={1.5} />
                创建
              </Button>
              <Button variant="secondary" onPress={() => void refetch()}>
                <RefreshCw className="size-4" stroke={1.5} />
                刷新
              </Button>
            </div>
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<Product>>
            {({ row }) => {
              const product = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => handleEdit(product)}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="sku"
                          textValue="SKU / 价格"
                          onAction={() => handlePriceSetting(product)}
                        >
                          <Label>SKU / 价格</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(product)}
                        >
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <ProductEditorModal
        open={editorModalOpen}
        product={editingProduct}
        onCancel={() => {
          setEditorModalOpen(false);
          setEditingProduct(null);
        }}
        onSuccess={handleEditorSuccess}
      />
      <SkuListModal
        open={priceModalOpen}
        productId={selectedProduct?.id}
        productName={selectedProduct?.name ?? undefined}
        productType={selectedProduct?.type ?? undefined}
        onCancel={() => {
          setPriceModalOpen(false);
          setSelectedProduct(null);
        }}
      />

      <AlertDialog isOpen={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除产品「{productToDelete?.name ?? productToDelete?.code}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setProductToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deletingProductId === productToDelete?.id}
                  onPress={() => void confirmDelete()}
                >
                  {deletingProductId === productToDelete?.id ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
